/**
 * ACTIVITY-CORE — the feed's DATA half.
 */
import { type Accessor, createMemo, createSignal } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@apunte-digital/powersync";

export interface ActivityRow {
  id: string;
  scope_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  action: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_device_id: string | null;
  platform: string | null;
  server_ts: string | null;
  client_ts: string | null;
  patch: string | null;
  batch_id: string | null;
  reverts_activity_id: string | null;
}

export interface ApiActivityRow {
  id: string;
  scopeId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action?: string | null;
  actorUserId?: string | null;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs?: string | null;
  clientTs?: string | null;
  patch?: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}

export function mapApiActivityRow(row: ApiActivityRow): ActivityRow {
  return {
    id: row.id,
    scope_id: row.scopeId ?? null,
    entity_type: row.entityType ?? null,
    entity_id: row.entityId ?? null,
    action: row.action ?? null,
    actor_user_id: row.actorUserId ?? null,
    actor_name: row.actorName ?? null,
    actor_device_id: row.actorDeviceId ?? null,
    platform: row.platform ?? null,
    server_ts: row.serverTs ?? null,
    client_ts: row.clientTs ?? null,
    patch: typeof row.patch === "string" ? row.patch : row.patch ? JSON.stringify(row.patch) : null,
    batch_id: row.batchId ?? null,
    reverts_activity_id: row.revertsActivityId ?? null,
  };
}

export interface ActivityHistoryPage {
  rows: ActivityRow[];
  hasMore: boolean;
}

export type ActivityFeedFilter = Record<string, unknown>;

export interface ActivityFeedSourceDeps {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  filter?: Accessor<Partial<ActivityFeedFilter>>;
  fetchOlder: (
    before: string | null,
    limit: number,
    filter: Partial<ActivityFeedFilter>,
  ) => Promise<ActivityHistoryPage | null>;
  matchesFilter?: (row: ActivityRow, filter: Partial<ActivityFeedFilter>) => boolean;
  localLimit?: number;
  pageSize?: number;
}

export const DEFAULT_LOCAL_FEED_LIMIT = 200;
export const DEFAULT_API_PAGE_SIZE = 50;

export function createActivityFeedSource(deps: ActivityFeedSourceDeps): ActivityFeedSource {
  const pageSize = deps.pageSize ?? DEFAULT_API_PAGE_SIZE;

  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const filter = () => deps.filter?.() ?? {};

  const rows = createMemo<ActivityRow[]>(() => {
    const active = filter();
    return deps.matchesFilter
      ? olderRows().filter((r) => deps.matchesFilter?.(r, active) ?? true)
      : olderRows();
  });

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const current = rows();
      const last = current.at(-1);
      const page = await deps.fetchOlder(last?.id ?? null, pageSize, filter());
      if (!page) {
        return;
      }
      setOlderRows((prev) => [...prev, ...page.rows]);
      setHasMoreOlder(page.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  };

  return {
    rows,
    isLoading: () => loadingOlder() && olderRows().length === 0,
    isLoadingOlder: loadingOlder,
    hasMore: () => rows().length >= pageSize && hasMoreOlder(),
    loadOlder,
    pageSize,
  };
}

export interface ActivityFeedSource {
  rows: Accessor<ActivityRow[]>;
  isLoading: Accessor<boolean>;
  isLoadingOlder: Accessor<boolean>;
  hasMore: Accessor<boolean>;
  loadOlder: () => Promise<void>;
  pageSize: number;
}
